import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { Edit, Trash2, Send, Eye, MessageCircle, Download, FileText, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentCollection } from '@/types/documentCollection';
import apiService from '@/services/api';
import { buildRequirementList } from '@/lib/bgvRequirements';

interface DocumentCollectionTableProps {
  collections: DocumentCollection[];
  onEdit: (collection: DocumentCollection) => void;
  onDelete: (id: string) => void;
  onSendLink: (collection: DocumentCollection) => void;
  /** Called after a link was reissued, so the list can refresh. */
  onReissued?: () => void;
  loading: boolean;
  selectedCompanyId?: string;
  selectedCompanyName?: string;
  currentPage?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

const DocumentCollectionTable = ({
  collections,
  onEdit,
  onDelete,
  onSendLink,
  onReissued,
  loading,
  selectedCompanyId,
  selectedCompanyName,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
}: DocumentCollectionTableProps) => {
  const navigate = useNavigate();

  const handleSendWhatsApp = useCallback((collection: DocumentCollection) => {
    const verificationLink = collection.verificationLink || '';
    let phoneNumber = collection.phone.replace(/\D/g, '');
    if (!phoneNumber.startsWith('91') && phoneNumber.length === 10) {
      phoneNumber = '91' + phoneNumber;
    }

    const { items } = buildRequirementList(collection.formConfig);
    const requirementLines = items.map((item, i) => `${i + 1}. ${item}`).join('\n');

    const message = `Dear ${collection.name},

Greetings from Zella Screenings!

We are conducting a background verification on behalf of *${collection.companyName}*. As part of this process, we need you to complete the BGV Form and upload required documents.

Please click the link below to complete the form:
${verificationLink}

*You will need to provide:*
${requirementLines}

⏰ *Important:* This link will expire in 30 days.

If you have any questions, please contact us:
📧 Email: start@zellascreenings.com
📞 Phone: +91 7982938489 / +91 9871967859

Best regards,
*Team Zella Screenings*
SECURE | AUTHENTICATE`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  }, []);

  // New link on the latest form: archives progress, expires the old link.
  const handleReissue = useCallback(async (collection: DocumentCollection) => {
    if (!collection._id) return;
    if (!window.confirm(
      `Send ${collection.name} a new link on the latest form?\n\n` +
      'Their current link will stop working and they will fill the form from the beginning. ' +
      'Anything they entered so far is archived, not deleted.'
    )) return;
    try {
      const batchId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const response = await apiService.reissueDocumentCollectionLinks({ ids: [collection._id], batchId });
      const r = response.data || {};
      if (r.reissued) alert(r.emailFailures?.length ? 'New link created, but the email failed. Use Send Link to email it.' : 'New link sent.');
      else if (r.skippedRecentlySent) alert('A link was sent to this candidate moments ago. Try again in a minute.');
      else alert('No new link was sent (the candidate may have already submitted).');
      onReissued?.();
    } catch (error: any) {
      alert(error.message || 'Failed to send a new link');
    }
  }, [onReissued]);

  const handleDownloadDocx = useCallback(async (collection: DocumentCollection) => {
    if (!collection._id) return;
    try {
      const response = await apiService.downloadDocumentCollectionDocx(collection._id);
      if (response.success && response.data) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        alert('No generated DOCX found. Please generate it first from the detail page.');
      }
    } catch (error) {
      console.error('Download DOCX error:', error);
      alert('Failed to download DOCX');
    }
  }, []);

  const columns = useMemo<ColumnDef<DocumentCollection>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => {
          const date = row.original.createdAt;
          return date ? new Date(date).toLocaleDateString('en-IN') : '-';
        },
      },
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.getValue('code')}</span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Candidate',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.getValue('name')}</div>
            <div className="text-sm text-gray-500">{row.original.email}</div>
          </div>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
      },
      {
        id: 'bgvGroup',
        header: 'BGV Group',
        cell: ({ row }) => {
          // Populated to { _id, name } by the list endpoint; a bare id or null
          // otherwise. No group means the company default config.
          const group = row.original.bgvGroupId;
          const name = typeof group === 'object' && group ? group.name : null;
          return name
            ? <span className="text-sm text-gray-700">{name}</span>
            : <span className="text-sm text-gray-400">Company default</span>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.getValue('status') as string;
          const colors: Record<string, string> = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                colors[status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: 'verificationStatus',
        header: 'Verification',
        cell: ({ row }) => {
          const status = row.getValue('verificationStatus') as string;
          const colors: Record<string, string> = {
            not_initiated: 'bg-gray-100 text-gray-800',
            link_sent: 'bg-blue-100 text-blue-800',
            in_progress: 'bg-purple-100 text-purple-800',
            completed: 'bg-green-100 text-green-800',
            expired: 'bg-red-100 text-red-800',
          };
          const labels: Record<string, string> = {
            not_initiated: 'Not Started',
            link_sent: 'Link Sent',
            in_progress: 'In Progress',
            completed: 'Completed',
            expired: 'Expired',
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                colors[status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {labels[status] || status}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => row.original._id && navigate(`/document-collections/${row.original._id}`, {
                state: {
                  selectedCompanyId: selectedCompanyId || row.original.customerId,
                  selectedCompanyName: selectedCompanyName || row.original.companyName,
                }
              })}
              title="View Details"
              className="text-brand-green hover:text-brand-green-600 hover:bg-brand-green-50"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => row.original._id && navigate(`/document-collections/${row.original._id}/documents`, {
                state: {
                  selectedCompanyId: selectedCompanyId || row.original.customerId,
                  selectedCompanyName: selectedCompanyName || row.original.companyName,
                }
              })}
              title="View Documents"
              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
            >
              <FileText className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(row.original)}
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSendLink(row.original)}
              title="Send Link"
              disabled={row.original.verificationStatus === 'completed'}
            >
              <Send className="w-4 h-4" />
            </Button>
            {['link_sent', 'in_progress', 'expired'].includes(row.original.verificationStatus) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleReissue(row.original)}
                title="New link on latest form (expires the current link)"
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
            {row.original.verificationLink && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSendWhatsApp(row.original)}
                title="Send via WhatsApp"
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
            )}
            {row.original.generatedDocx?.s3Key && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadDocx(row.original)}
                title="Download DOCX"
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              >
                <Download className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => row.original._id && onDelete(row.original._id)}
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ),
      },
    ],
    [onEdit, onDelete, onSendLink, navigate, handleSendWhatsApp, handleReissue, handleDownloadDocx, selectedCompanyId, selectedCompanyName]
  );

  const _pageSize = pageSize ?? 10;
  const _currentPage = currentPage ?? 1;
  const pageCount = totalCount != null ? Math.ceil(totalCount / _pageSize) : -1;

  const table = useReactTable({
    data: collections,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount,
    state: {
      pagination: {
        pageIndex: _currentPage - 1,
        pageSize: _pageSize,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function' && onPageChange) {
        const newState = updater({ pageIndex: _currentPage - 1, pageSize: _pageSize });
        onPageChange(newState.pageIndex + 1);
      }
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-500">Loading...</div>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
        <div className="text-lg font-medium text-gray-500">No document collections found</div>
        <div className="text-sm text-gray-400 mt-2">Click "Add Collection" to create one</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {(totalCount ?? collections.length) === 0 ? 0 : (_currentPage - 1) * _pageSize + 1} to{' '}
          {Math.min(
            _currentPage * _pageSize,
            totalCount ?? collections.length
          )}{' '}
          of {totalCount ?? collections.length} results
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentCollectionTable;
